/**
 * Vendor Merge Tool
 * Detects duplicate vendor records via pg_trgm similarity and lets an accountant
 * choose which record to keep, then merges all FK references.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { queryOptions } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Party } from "@/lib/types";
import { codeFor } from "../lib/displayCode";

export const Route = createFileRoute("/_auth/vendors/merge-tool")({
  component: VendorMergeToolPage,
});

interface MergeCandidatePair {
  party_a: Party;
  party_b: Party;
  similarity_score: number;
}

interface MergeResult {
  target: Party;
  refs_updated: Record<string, number>;
  source_deactivated: boolean;
}

const mergeCandidatesQueryOptions = (minScore: number) =>
  queryOptions({
    queryKey: ["merge-candidates", "vendor", minScore],
    queryFn: () =>
      api<MergeCandidatePair[]>(
        `/api/parties/merge-candidates?party_type=vendor&min_score=${minScore}`,
      ),
  });

function ScoreBadge({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color =
    score >= 0.8
      ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
      : score >= 0.6
        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
        : "bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-slate-300";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {pct}% match
    </span>
  );
}

function PartyCard({
  party,
  label,
  selected,
  onClick,
}: {
  party: Party;
  label: "Keep" | "Merge Into" | "Remove";
  selected: boolean;
  onClick: () => void;
}) {
  const labelColor =
    label === "Keep"
      ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
      : label === "Remove"
        ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
        : "bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-slate-300";

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
        selected
          ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30"
          : "border-slate-200 dark:border-gray-700 hover:border-blue-300"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className={`px-1.5 py-0.5 text-xs rounded font-medium ${labelColor}`}
        >
          {label}
        </span>
        <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
          {codeFor(party)}
        </span>
      </div>
      <div className="font-medium text-slate-900 dark:text-slate-100 text-sm">
        {party.name}
      </div>
      {(party.city || party.address_line1) && (
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {[party.address_line1, party.city, party.state]
            .filter(Boolean)
            .join(", ")}
        </div>
      )}
      {party.email && (
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {party.email}
        </div>
      )}
    </button>
  );
}

function MergePairRow({ pair }: { pair: MergeCandidatePair }) {
  const qc = useQueryClient();
  // null = no winner chosen; "a" = keep A; "b" = keep B
  const [winner, setWinner] = useState<"a" | "b" | null>(null);
  const [merged, setMerged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MergeResult | null>(null);

  const mergeMut = useMutation({
    mutationFn: (payload: { source_id: string; target_id: string }) =>
      api<MergeResult>("/api/parties/merge", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      setMerged(true);
      setResult(data);
      setError(null);
      // Invalidate any vendor lists
      qc.invalidateQueries({ queryKey: ["parties"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (merged && result) {
    const total = Object.values(result.refs_updated).reduce((a, b) => a + b, 0);
    return (
      <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
          <span>✓</span>
          <span>
            Merged into <strong>{result.target.name}</strong> — {total} reference
            {total !== 1 ? "s" : ""} updated. Source deactivated.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-3">
        <ScoreBadge score={pair.similarity_score} />
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Click a record below to choose which to keep. The other will be
          deactivated.
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <PartyCard
          party={pair.party_a}
          label={winner === "a" ? "Keep" : winner === "b" ? "Remove" : "Merge Into"}
          selected={winner === "a"}
          onClick={() => setWinner(winner === "a" ? null : "a")}
        />
        <PartyCard
          party={pair.party_b}
          label={winner === "b" ? "Keep" : winner === "a" ? "Remove" : "Merge Into"}
          selected={winner === "b"}
          onClick={() => setWinner(winner === "b" ? null : "b")}
        />
      </div>

      {error && (
        <div className="text-xs text-red-600 mb-2 bg-red-50 dark:bg-red-950 rounded px-2 py-1">
          {error}
        </div>
      )}

      {winner && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const target =
                winner === "a" ? pair.party_a.id : pair.party_b.id;
              const source =
                winner === "a" ? pair.party_b.id : pair.party_a.id;
              mergeMut.mutate({ source_id: source, target_id: target });
            }}
            disabled={mergeMut.isPending}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            {mergeMut.isPending
              ? "Merging…"
              : `Merge & keep ${winner === "a" ? pair.party_a.name : pair.party_b.name}`}
          </button>
          <button
            onClick={() => setWinner(null)}
            className="px-3 py-1.5 text-sm border border-slate-300 dark:border-gray-700 rounded text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function VendorMergeToolPage() {
  const [minScore, setMinScore] = useState(0.5);
  const candidatesQ = useQuery(mergeCandidatesQueryOptions(minScore));
  const pairs = candidatesQ.data ?? [];

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Link
            to="/vendors"
            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
          >
            ← Vendors
          </Link>
        </div>
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Vendor Merge Tool
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Detects potential duplicate vendor records using name similarity.
          Choose which record to keep — all AP invoices, payments, POs, and
          subcontracts are re-linked automatically.
        </p>
      </div>

      {/* Threshold control */}
      <div className="flex items-center gap-3 mb-5 bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg px-4 py-3">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
          Similarity threshold:
        </label>
        <input
          type="range"
          min={0.3}
          max={0.95}
          step={0.05}
          value={minScore}
          onChange={(e) => setMinScore(parseFloat(e.target.value))}
          className="flex-1"
        />
        <span className="text-sm font-mono text-slate-700 dark:text-slate-300 w-12">
          {Math.round(minScore * 100)}%
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Lower = more results
        </span>
      </div>

      {candidatesQ.isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Scanning for duplicates…
        </div>
      )}
      {candidatesQ.error && (
        <div className="text-sm text-red-600">
          {(candidatesQ.error as Error).message}
        </div>
      )}

      {!candidatesQ.isLoading && pairs.length === 0 && (
        <div className="text-sm text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg px-4 py-8 text-center">
          🎉 No duplicate vendors found at {Math.round(minScore * 100)}%
          similarity or above.
          <br />
          <span className="text-xs mt-1 block">
            Lower the threshold to see more potential matches.
          </span>
        </div>
      )}

      {pairs.length > 0 && (
        <>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            {pairs.length} potential duplicate pair
            {pairs.length !== 1 ? "s" : ""} found
          </p>
          <div className="space-y-3">
            {pairs.map((pair) => (
              <MergePairRow
                key={`${pair.party_a.id}-${pair.party_b.id}`}
                pair={pair}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
